import catalogLogo from "../../assets/logo.svg";
import { splitIntoColumns } from "../utils/catalogColumns";
import { resolveGroupThemeKey } from "../utils/catalogThemes";

const GROUP_THEME_PALETTE = [
  { key: "sky", headerBg: "#edf5ff", headerBorder: "#cfe1ff", headerText: "#2563eb" },
  { key: "mint", headerBg: "#ecfbf3", headerBorder: "#c9f0dd", headerText: "#059669" },
  { key: "amber", headerBg: "#fff7ea", headerBorder: "#f7dfb1", headerText: "#d97706" },
  { key: "rose", headerBg: "#fff0f4", headerBorder: "#f7c8d6", headerText: "#e11d48" },
  { key: "violet", headerBg: "#f4efff", headerBorder: "#d9cdfd", headerText: "#7c3aed" },
  { key: "teal", headerBg: "#ecfbfc", headerBorder: "#bce9eb", headerText: "#0f766e" },
];

const getGroupTheme = (group) => {
  const themeKey = resolveGroupThemeKey(group);
  return (
    GROUP_THEME_PALETTE.find((theme) => theme.key === themeKey) || GROUP_THEME_PALETTE[0]
  );
};

const PRINT_STYLES = `
  @page {
    size: A4 portrait;
    margin: 8mm 4mm;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    background: #fff;
    color: #57606a;
  }
  .pricing-catalog-print-root {
    margin: 0;
    padding: 4px;
    width: 100%;
  }
  .print-page {
    box-sizing: border-box;
    width: 100%;
    max-width: 100%;
    margin: 0;
    background: #ffffff;
    padding: 8px 2px;
  }
  .print-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
    padding: 0 2px;
  }
  .print-header .left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .print-title {
    font-size: 14px;
    font-weight: 600;
    color: #424a53;
  }
  .print-contacts {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }
  .print-contact {
    position: relative;
    padding-left: 12px;
    font-size: 12px;
    color: #000;
  }
  .print-contact::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    background: #2f80ed;
    border-radius: 2px;
  }
  .print-meta {
    margin-top: 6px;
    font-size: 11px;
    color: #6e7781;
    text-align: right;
  }
  .catalog-print-grid {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    column-gap: 2px;
    width: 100%;
  }
  .catalog-print-column {
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1 1 0;
    min-width: 0;
  }
  .group-table {
    width: 100%;
    min-width: 0;
    margin: 0 0 8px;
    border: 1px solid #dce3eb;
    border-radius: 8px;
    background: #fff;
  }
  .group-table .catalog-print-group-header {
    padding: 10px 8px;
    font-weight: 700;
    font-size: 13px;
    line-height: 16px;
    border-bottom: 1px solid #dce3eb;
    border-radius: 8px 8px 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .group-table table {
    width: 100%;
    table-layout: auto;
    border-collapse: collapse;
    font-size: 12px;
    background: #ffffff;
  }
  .group-table thead {
    display: none;
  }
  .group-table tbody tr {
    min-height: 36px;
    height: auto;
    background: #ffffff;
  }
  .group-table th,
  .group-table td {
    padding: 4px 4px;
    border-bottom: 1px solid #e9eef2;
    color: #57606a;
    vertical-align: middle;
  }
  .group-table td.product-name {
    width: auto;
    max-width: 55%;
    padding-right: 2px;
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    line-height: 1.25;
  }
  .group-table td.variant {
    width: 1%;
    white-space: nowrap;
    text-align: right;
    padding-left: 4px;
    padding-right: 2px;
    font-size: 11px;
    color: #6e7781;
    overflow: visible;
  }
  .group-table td.price {
    width: 1%;
    font-weight: 700;
    text-align: right;
    padding-left: 0;
    padding-right: 4px;
    white-space: nowrap;
    overflow: visible;
  }
  .group-table tbody tr:last-child td {
    border-bottom: 0;
  }
  .group-table-empty td {
    text-align: center;
    color: #8b95a3;
    font-size: 13px;
    padding: 16px 12px;
  }
  @media print {
    @page {
      size: A4 portrait;
      margin: 8mm 4mm;
    }
    html, body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .pricing-catalog-print-root {
      padding: 0;
    }
    .print-page {
      width: 100%;
      max-width: 100%;
      margin: 0;
      padding: 0;
      box-shadow: none;
    }
    /* Each column paginates on its own — do NOT use display:table (one row = no split). */
    .catalog-print-grid {
      display: block;
      width: 100%;
    }
    .catalog-print-grid::after {
      content: "";
      display: block;
      clear: both;
    }
    .catalog-print-column {
      display: block;
      float: left;
      width: 33.33%;
      box-sizing: border-box;
      padding: 0 1px;
      vertical-align: top;
    }
    /* Let groups and rows flow onto the next page naturally. */
    .group-table,
    .group-table table,
    .group-table tbody,
    .group-table tbody tr,
    .group-table td {
      break-inside: auto;
      page-break-inside: auto;
    }
  }
`;

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const fmtPrice = (symbol, value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  const sym = escapeHtml(symbol || "€");
  return `${sym}${Math.round(num)}`;
};

const resolveLogoUrl = () => {
  if (!catalogLogo) return "";
  if (String(catalogLogo).startsWith("http")) return catalogLogo;
  return `${window.location.origin}${catalogLogo}`;
};

/** Renders HTML for print / Save as PDF. Expects `groups` array from PricingCatalog. */
export function renderPrintHtml(groups = []) {
  const logoUrl = resolveLogoUrl();
  const logoSvg = logoUrl
    ? `<img src="${escapeHtml(logoUrl)}" alt="Mr.Phone" style="height: 36px; width: auto; max-width: 200px; object-fit: contain;" />`
    : "";

  const renderGroup = (group) => {
    const theme = getGroupTheme(group);
    const products = group.products || [];
    const rows = products.length
      ? products
          .map(
            (product) => `
      <tr>
        <td class="product-name">${escapeHtml(product.name || "")}</td>
        <td class="variant">${escapeHtml(product.variant || "")}</td>
        <td class="price">${fmtPrice(group.currencySymbol, product.price)}</td>
      </tr>`
          )
          .join("")
      : `<tr class="group-table-empty"><td colspan="3">No products in this group</td></tr>`;

    return `
      <div class="group-table">
        <div
          class="catalog-print-group-header"
          style="background:${theme.headerBg};border-color:${theme.headerBorder};color:${theme.headerText};"
        >
          ${escapeHtml(group.name || "Group")}
        </div>
        <div class="group-body">
          <table>
            <thead>
              <tr><th>Product</th><th>Variant</th><th>Price</th></tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`;
  };

  const printDate = new Date().toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const groupsGridHtml =
    groups.length === 0
      ? '<p style="text-align:center;color:#8b95a3;padding:40px 0;">No product groups to export.</p>'
      : `<div class="catalog-print-grid">${splitIntoColumns(groups)
          .map(
            (columnGroups) =>
              `<div class="catalog-print-column">${columnGroups
                .map(({ item: group }) => renderGroup(group))
                .join("\n")}</div>`
          )
          .join("\n")}</div>`;

  const pagesHtml = `
      <div class="print-page">
        <div class="print-header">
          <div class="left">
            ${logoSvg}
            <div class="print-title">Mobile Pricing Catalog</div>
          </div>
          <div>
            <div class="print-contacts">
              <div class="print-contact">+49 1577 777 3887 (WhatsApp available)</div>
              <div class="print-contact">Lärchenstraße 78, 65933 Frankfurt am Main</div>
              <div class="print-contact">info@mrphone.gmbh</div>
            </div>
            <div class="print-meta">${escapeHtml(printDate)}</div>
          </div>
        </div>
        ${groupsGridHtml}
      </div>`;

  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>Print - Pricing Catalog</title>
        <style>${PRINT_STYLES}</style>
      </head>
      <body>
        <div class="pricing-catalog-print-root">
          ${pagesHtml}
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.focus();
              window.print();
            }, 300);
          };
          window.onafterprint = function() {
            window.close();
          };
        </script>
      </body>
    </html>
  `;
}

export default function PrintView() {
  return null;
}
